import { getGroupsContaining } from '@/apis/groups';
import {
  FetchAutocomplete,
  FetchAutocompleteChangeReason,
  domainCodeAtom,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionField } from '@components/DrawerAccordion';
import { Search } from '@mui/icons-material';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { labelSorting } from '@/util/labelSorting';

// The GroupRecord[] returned from getGroupsContaining has title, but we want
// label
export interface GroupRecordResult {
  id: number;
  label: string;
}

type GroupsFetchAutocompleteProps = {
  onChange: (
    newValue: GroupRecordResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  value: GroupRecordResult[];
  minLength?: number;
  groupLabel?: string;
  dataTestidPrefix?: string;
  loadingText?: string;
  noOptionsText?: string;
  error?: boolean;
  helperText?: React.ReactNode;
};

export const useLookupGroups = () => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useCallback(
    async (lookupValue: string) => {
      const token = await getAccessTokenSilently();
      return (await getGroupsContaining(token, lookupValue, domainCode))
        .map((x) => {
          return {
            id: x.id,
            label: x.title,
          };
        })
        .sort(labelSorting);
    },
    [domainCode, getAccessTokenSilently]
  );
};

const GroupsFetchAutocomplete = ({
  value = [] as GroupRecordResult[],
  onChange,
  minLength = 3,
  groupLabel = 'Find a group',
  dataTestidPrefix = 'getfiltergroups',
  loadingText = 'Looking for groups...',
  noOptionsText = 'No groups found',
  error = false,
  helperText = '',
}: GroupsFetchAutocompleteProps) => {
  const lookupGroups = useLookupGroups();

  return (
    <AccordionField>
      <FetchAutocomplete
        lookup={lookupGroups}
        minLength={minLength}
        label={groupLabel}
        value={value}
        onChange={onChange}
        data-testid={dataTestidPrefix}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        popupIcon={<Search />}
        disableIconFlip
        error={error}
        helperText={helperText}
      />
    </AccordionField>
  );
};

export default GroupsFetchAutocomplete;
